import { useEffect, useState } from "react";
import { CalendarCheck, Send, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { recapPreview, sendRecap } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";

export const WeeklyRecap = ({ refreshKey }) => {
  const [recap, setRecap] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    recapPreview().then(setRecap).catch(() => setRecap(null));
  }, [refreshKey]);

  const send = async () => {
    setBusy(true);
    try {
      await sendRecap();
      toast.success("Recap emailed to atfq.pcast@gmail.com");
    } catch (err) {
      toast.error(extractApiError(err, "Could not send recap."));
    } finally {
      setBusy(false);
    }
  };

  if (!recap) return null;

  return (
    <section data-testid="scout-recap" className="border-t border-slate-200 pt-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="text-xs tracking-tight text-[var(--accent)] flex items-center gap-2">
          <CalendarCheck className="w-3.5 h-3.5" /> Weekly Recap · auto-emails Sundays 8 AM ET
        </div>
        <button
          type="button"
          data-testid="scout-recap-send"
          onClick={send}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--accent)] hover:text-[#0A192F] disabled:opacity-50"
        >
          <Send className="w-3 h-3" /> {busy ? "Sending…" : "Email it now"}
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[
          ["New", recap.new_count],
          ["Shortlisted", recap.shortlisted.length],
          ["Contacted", recap.contacted.length],
          ["Booked", recap.booked.length],
        ].map(([label, n]) => (
          <div key={label} className="border border-slate-200 py-2">
            <div className="font-display text-xl text-[#0A192F]">{n}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
          </div>
        ))}
      </div>
      {recap.listener_leaders_all?.length > 0 && (
        <div data-testid="scout-recap-leaders" className="mt-4 border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
            <ThumbsUp className="w-3 h-3 text-[var(--accent)]" /> Listeners want
          </div>
          <ol className="space-y-1">
            {recap.listener_leaders_all.map((l, i) => {
              const wk = recap.listener_leaders_week?.find((w) => w.id === l.id);
              return (
                <li key={l.id} className="flex items-baseline gap-2 text-sm">
                  <span className="text-xs text-slate-400 w-4">{i + 1}.</span>
                  <span className="text-[#0A192F] font-medium">{l.name}</span>
                  <span className="text-xs text-slate-500 ml-auto">
                    {l.votes} total{wk ? ` · +${wk.votes} this week` : ""}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      )}
      {recap.nudges.length > 0 && (
        <ul data-testid="scout-recap-nudges" className="mt-4 space-y-2">
          {recap.nudges.map((n) => (
            <li key={n.id} className="text-sm border-l-2 border-[var(--accent)] pl-3">
              <span className="text-[#0A192F] font-medium">{n.name}</span>
              <div className="text-xs text-slate-600">{n.nudge}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default WeeklyRecap;
